import React, { useState } from "react";
import { toast } from "sonner";
import { Check, MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ApprovalBadge } from "@/components/platform/Badges";
import { api } from "@/lib/api";
import { apiError, fmtD, fmtDT } from "@/lib/platform";

const ACTION_LABEL = { requested: "Requested", approved: "Approved", rejected: "Rejected", commented: "Commented", cancelled: "Cancelled" };
const ACTION_DOT = { requested: "bg-purple-400", approved: "bg-emerald-500", rejected: "bg-red-500", commented: "bg-amber-400", cancelled: "bg-slate-400" };

export const ApprovalHistory = ({ history = [], testId }) => (
  <ol className="space-y-2" data-testid={testId}>
    {history.map((h, i) => (
      <li key={i} className="flex gap-2 text-[12px]">
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${ACTION_DOT[h.action] || "bg-slate-300"}`} />
        <span>
          <span className="font-semibold text-[#003366]">{ACTION_LABEL[h.action] || h.action}</span>
          <span className="text-slate-500"> · {h.actor} · {fmtDT(h.at)}</span>
          {h.comment && <span className="block text-slate-600 italic">“{h.comment}”</span>}
        </span>
      </li>
    ))}
  </ol>
);

export const ApprovalPanel = ({ approval, onChanged, currentUser }) => {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const a = approval;

  const act = async (kind) => {
    if (kind === "reject" && !comment.trim()) {
      toast.error("Please give a reason for rejecting");
      return;
    }
    if (kind === "comment" && !comment.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/approvals/${a.id}/${kind}`, { comment: comment.trim() || null });
      toast.success(kind === "comment" ? "Comment added" : `Request ${data.status.toLowerCase()}`);
      setComment("");
      onChanged?.(data);
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4 space-y-3" data-testid={`approval-panel-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-slate-500">{a.approval_ref} · {a.module} · {a.object_type} {a.object_label}</div>
          <div className="text-sm font-semibold text-[#003366]">{a.title}</div>
          {a.description && <div className="text-xs text-slate-600 mt-1">{a.description}</div>}
          <div className="text-[11px] text-slate-500 mt-1">
            Requested by {a.requested_by} · {fmtDT(a.requested_at)}
            {a.due_date && <> · decide by <span className="font-semibold">{fmtD(a.due_date)}</span></>}
          </div>
          <div className="text-[11px] text-slate-500">
            Approvers: {a.approvers?.length ? a.approvers.join(", ") : "any eligible approver"}
          </div>
        </div>
        <ApprovalBadge status={a.status} testId={`approval-status-${a.id}`} />
      </div>
      <ApprovalHistory history={a.history} testId={`approval-history-${a.id}`} />
      <div className="space-y-2">
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder={a.can_decide ? "Comment or reason (required to reject)" : "Add a comment"}
                  data-testid={`approval-comment-input-${a.id}`} />
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => act("comment")} disabled={busy || !comment.trim()}
                  data-testid={`approval-comment-btn-${a.id}`}>
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Comment
          </Button>
          {a.can_decide && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve")} disabled={busy}
                      data-testid={`approval-approve-btn-${a.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => act("reject")} disabled={busy}
                      data-testid={`approval-reject-btn-${a.id}`}>
                <X className="w-3.5 h-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
          {a.status === "Pending" && a.requested_by === currentUser && (
            <Button size="sm" variant="ghost" onClick={() => act("cancel")} disabled={busy}
                    data-testid={`approval-cancel-btn-${a.id}`}>
              Withdraw request
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
